/**
 * @fileoverview Single widget card (`CanvasWidgetCard`) on the edit-mode `DashboardCanvasBuilder`
 * canvas, featuring drag handle, enabled checkbox, sample-data preview toggle, and `⋮` overflow
 * menu (`Duplicate`, `Move up`, `Move down`, `Delete`).
 * @module ui-admin/components/DashboardCanvasBuilder/CanvasWidgetCard
 *
 * Responsibilities: - Render the widget card chrome on the edit-mode canvas.
 *                   - Provide per-widget overflow menu (closes gap G-11) and sample-data toggle (G-13).
 *                   - Render the widget body via `WidgetRenderer`.
 * Dependencies:     - React, lucide-react
 *                   - @firesquirrel/local-db (DashboardWidget)
 *                   - ../dashboard/WidgetRenderer (WidgetRenderer, FilterValue)
 *                   - ./widgetTypes (WIDGET_TYPES)
 *                   - ./dashboardTourSteps (DASHBOARD_TOUR_TARGET)
 * Architecture:     - Presentation-only card component; delegates all mutations to parent callbacks.
 * Notes:            - Closes gaps G-11 and G-13.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  GripVertical,
  MoreVertical,
  Copy,
  ArrowUp,
  ArrowDown,
  Trash2,
  Eye,
  BarChart3,
  HelpCircle,
} from 'lucide-react';
import type { DashboardWidget } from '@firesquirrel/local-db';
import { WidgetRenderer, type FilterValue } from '../dashboard/WidgetRenderer';
import { WIDGET_TYPES } from './widgetTypes';
import { DASHBOARD_TOUR_TARGET } from './dashboardTourSteps';

/**
 * @contract CanvasWidgetCardProps
 * Purpose: Props for `CanvasWidgetCard`.
 * Fields:  widget, isSelected, isFirst?, onSelect, onDelete, onDuplicate, onMoveUp, onMoveDown,
 *          onToggleEnabled?, onToggleSample?, showSample?, data?, readOnly?, refreshKey?,
 *          filterValues?, onFilterChange?, lookupOptions?
 * Rules:   Presentation-only; no persistence.
 * Sync:    N/A.
 */
export interface CanvasWidgetCardProps {
  widget: DashboardWidget;
  isSelected: boolean;
  isFirst?: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleEnabled?: (enabled: boolean) => void;
  onToggleSample?: () => void;
  showSample?: boolean;
  data?: Record<string, unknown>[];
  readOnly?: boolean;
  refreshKey?: number;
  filterValues?: Record<string, FilterValue | null>;
  onFilterChange?: (filterId: string, value: FilterValue | null) => void;
  lookupOptions?: Array<{ value: string; label: string }>;
}

const DYNAMIC_WIDGET_TYPES = new Set(['kpi', 'chart', 'table', 'list', 'gauge', 'progressBar']);

/**
 * @component CanvasWidgetCard
 * Purpose:     Render a single widget on the dashboard canvas with overflow actions and preview.
 * Props:       CanvasWidgetCardProps.
 * Behavior:    Grip + icon + title + data-source badge + sample toggle + overflow `⋮` menu + `WidgetRenderer` body.
 * Data Source: none (presentation-only; `data` passed via props).
 * Constraints: no persistence; calls back to the canvas.
 * Performance: stateless except local overflow menu open boolean.
 */
export function CanvasWidgetCard({
  widget,
  isSelected,
  isFirst = false,
  onSelect,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onToggleEnabled,
  onToggleSample,
  showSample = true,
  data,
  readOnly = false,
  refreshKey,
  filterValues,
  onFilterChange,
  lookupOptions,
}: CanvasWidgetCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  // Close the overflow menu / help popover on outside click or Escape.
  useEffect(() => {
    if (!menuOpen && !helpOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuOpen && menuRef.current && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
      if (helpOpen && helpRef.current && !helpRef.current.contains(target)) {
        setHelpOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setHelpOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, helpOpen]);
  const widgetType = WIDGET_TYPES.find((w) => w.type === widget.type);
  const Icon = widgetType?.icon ?? BarChart3;
  const isDynamic = DYNAMIC_WIDGET_TYPES.has(widget.type);
  const customStyle = widget.style ?? {};

  return (
    <div
      data-testid={`canvas-widget-card-${widget.id}`}
      data-tour={isFirst ? DASHBOARD_TOUR_TARGET.widgetCard : undefined}
      onClick={() => {
        if (!readOnly) onSelect();
      }}
      style={{
        backgroundColor: customStyle.backgroundColor || '#ffffff',
        borderColor: isSelected ? '#ea580c' : (customStyle.borderColor || '#e5e7eb'),
        borderWidth: customStyle.borderWidth ?? (isSelected ? 2 : 1),
        textAlign: customStyle.textAlign,
      }}
      className={`relative group h-full flex flex-col p-3 rounded-lg border transition-all ${
        readOnly ? '' : 'cursor-pointer'
      } ${
        isSelected && !readOnly
          ? 'ring-2 ring-orange-200 shadow-sm'
          : 'hover:border-gray-300 shadow-2xs'
      } ${widget.enabled === false ? 'opacity-50' : ''}`}
    >
      {/* Header chrome */}
      <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {!readOnly && (
            <div className="drag-handle cursor-move text-gray-400 hover:text-gray-600 shrink-0">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          {!readOnly && onToggleEnabled && (
            <input
              type="checkbox"
              aria-label={`Enable ${widget.title}`}
              checked={widget.enabled !== false}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-orange-600 shrink-0"
            />
          )}
          <Icon className="h-4 w-4 text-gray-500 shrink-0" />
          <h4
            className="font-semibold text-gray-900 text-xs truncate flex-1 min-w-0"
            style={{ color: customStyle.headerColor, textAlign: customStyle.textAlign }}
          >
            {widget.title}
          </h4>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {widget.dataSource?.cubeId && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              {widget.dataSource.cubeId}
            </span>
          )}

          {!readOnly && isDynamic && onToggleSample && (
            <button
              type="button"
              aria-label="Toggle sample preview"
              title={showSample ? 'Hide sample preview' : 'Show sample preview'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSample();
              }}
              className={`p-1 rounded text-xs transition-colors ${
                showSample ? 'text-orange-600 bg-orange-50' : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          )}

          {!readOnly && (
            <div className="relative" ref={helpRef}>
              <button
                type="button"
                aria-label={`Help for ${widget.title}`}
                data-testid={`widget-help-btn-${widget.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setHelpOpen((prev) => !prev);
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
              {helpOpen && (
                <div
                  role="dialog"
                  aria-label={`${widgetType?.label ?? widget.type} help`}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-7 z-30 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs space-y-1"
                >
                  <div className="font-semibold text-gray-900">
                    {widgetType?.label ?? widget.type}
                  </div>
                  <p className="text-gray-600">{widgetType?.description}</p>
                  <div className="text-[11px] text-gray-400">
                    {`Recommended size: ${widgetType?.defaultW ?? 4}×${widgetType?.defaultH ?? 2} cols`}
                  </div>
                </div>
              )}
            </div>
          )}

          {!readOnly && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                aria-label={`Actions for ${widget.title}`}
                data-testid={`widget-overflow-btn-${widget.id}`}
                data-tour={isFirst ? DASHBOARD_TOUR_TARGET.overflowMenu : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((prev) => !prev);
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
              >
                <MoreVertical className="h-4 w-4" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  data-testid={`widget-overflow-menu-${widget.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-7 z-30 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-xs"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onDuplicate();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-700 hover:bg-gray-50"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Duplicate
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onMoveUp();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                    Move up
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onMoveDown();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                    Move down
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Widget Content Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {showSample || readOnly ? (
          <WidgetRenderer
            widget={widget}
            data={data}
            refreshKey={refreshKey}
            filterValues={filterValues}
            onFilterChange={onFilterChange}
            lookupOptions={lookupOptions}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded">
            {`${widgetType?.label ?? widget.type} (${widget.layout.w}×${widget.layout.h})`}
          </div>
        )}
      </div>
    </div>
  );
}

export default CanvasWidgetCard;
